<section class="max-w-[1320px] mx-auto px-4 sm:px-6 lg:px-10 py-8 sm:py-12 animate-fade-in relative z-10 pb-24 overflow-x-hidden">

  <!-- ================= HEADER SECTION ================= -->
  <div class="mb-14 sm:mb-20 text-center max-w-3xl mx-auto">
    <span
      class="inline-block text-gold text-[10.5px] sm:text-xs uppercase tracking-widest font-extrabold bg-gold/10 px-3.5 py-2 rounded-full border border-gold/25 shadow-[0_0_15px_rgba(245,183,59,0.08)]">
      DEX Protocol Advantages
    </span>
    <h1
      class="font-display font-black text-[28px] sm:text-[40px] md:text-[48px] uppercase tracking-wider text-white mt-5 leading-tight">
      Empowering Token Creators &
      <span class="block sm:inline text-gold text-glow-gold">Amplifying Yields</span>
    </h1>
    <p class="text-text-muted text-[13.5px] sm:text-[15.5px] mt-4 leading-relaxed max-w-2xl mx-auto">
      DEX introduces a dual-engine architecture: 4 automated income streams to launch new tokens, combined with a double-compounding concentrated liquidity model.
    </p>
  </div>

  <!-- ================= SECTION 1: 4 INCOME STREAMS ================= -->
  <div class="mb-24 sm:mb-32 relative">
    
    <!-- Clean Homepage-Style Header -->
    <div class="text-center mb-16 flex flex-col items-center gap-4 relative z-10">
      <h2 class="font-display font-black text-2xl sm:text-3xl text-white uppercase tracking-wider">
        4 Income Streams for New Token Creators
      </h2>
      <span class="w-16 h-1 bg-gold rounded-full shadow-[0_0_10px_rgba(245,183,59,0.5)]"></span>
      <p class="text-[11px] sm:text-xs text-gold uppercase tracking-[0.2em] font-extrabold mt-1">
        Powered by the DEX Protocol
      </p>
    </div>

    <!-- Connecting Grid Lines (Subtle SVG Background layer) -->
    <svg class="hidden xl:block absolute inset-0 w-full h-full pointer-events-none z-0" viewBox="0 0 1000 650" preserveAspectRatio="none">
      <!-- Radiating dotted lines -->
      <line x1="500" y1="360" x2="250" y2="195" stroke="#F5B73B" stroke-width="1" stroke-dasharray="5 5" opacity="0.18" />
      <line x1="500" y1="360" x2="750" y2="195" stroke="#F5B73B" stroke-width="1" stroke-dasharray="5 5" opacity="0.18" />
      <line x1="500" y1="360" x2="250" y2="525" stroke="#F5B73B" stroke-width="1" stroke-dasharray="5 5" opacity="0.18" />
      <line x1="500" y1="360" x2="750" y2="525" stroke="#F5B73B" stroke-width="1" stroke-dasharray="5 5" opacity="0.18" />
      <!-- Center ring -->
      <circle cx="500" cy="360" r="110" fill="none" stroke="#F5B73B" stroke-width="1" stroke-dasharray="4 4" opacity="0.12" />
    </svg>

    <!-- 4 quadrant cards arranged with Centerpiece inside grid -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-x-12 gap-y-16 relative z-10 items-stretch">

      <!-- Centerpiece Brand Coin placed static in grid center -->
      <div class="hidden xl:flex absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-48 h-48 items-center justify-center z-20 pointer-events-none">
        <div class="w-full h-full flex items-center justify-center relative">
          <div class="absolute inset-0 bg-gold/15 rounded-full blur-2xl"></div>
          <div class="w-38 h-38 flex items-center justify-center">
            <img src="/dex-coin-logo.png" alt="DEX" class="w-full h-full object-contain" />
          </div>
        </div>
      </div>

      <!-- [CARD 1 IN HTML FLOW] - STREAM 1: AT LAUNCH (Desktop: lg:order-2 -> Top Right) -->
      <div class="relative border border-white/[0.05] bg-[#0c0c0c]/75 p-6 sm:p-8 rounded-[20px] flex flex-col justify-between shadow-[0_4px_30px_rgba(0,0,0,0.2)] hover:border-gold/30 hover:shadow-[0_0_30px_rgba(245,183,59,0.06)] transition-all duration-300 lg:min-h-[415px] lg:order-2">
        <!-- Badge on Border -->
        <div class="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-gold/10 text-gold text-[9px] font-black uppercase tracking-widest px-4 py-1 border border-gold/30 backdrop-blur-md rounded-md whitespace-nowrap">
          At Launch
        </div>

        <div class="mt-2">
          <h3 class="font-display font-black text-white text-[15px] uppercase tracking-wider mb-4">
            1. AUDIT & INITIAL REVENUE
          </h3>
          <p class="text-text-muted text-[13px] leading-relaxed mb-6">
            <strong>Launch & Audit:</strong> Token is audited. Earns Daily Fees from DEX's high-volume ETH/USDC concentrated liquidity pools immediately.
          </p>
        </div>

        <!-- Visual layout replicating the Auditing Portal -->
        <div class="bg-black/45 border border-white/[0.03] rounded-xl p-4 flex flex-col gap-4">
          <div class="flex items-center justify-between text-[9px] font-bold text-text-dim uppercase tracking-wider px-1">
            <span>New Token e.g.</span>
            <span>DEX Audit</span>
            <span>ETH/USDC Concentrated Liquidity</span>
          </div>

          <div class="flex items-center justify-between gap-2.5">
            <!-- Rocket Shield Symbol -->
            <div class="w-11 h-11 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center shrink-0 text-blue-400">
              <svg class="w-5.5 h-5.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
              </svg>
            </div>
            
            <!-- Lightning electrical wire -->
            <div class="flex-1 h-[2px] bg-gradient-to-r from-blue-500 to-amber-500 relative animate-pulse">
              <span class="absolute right-0 top-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-amber-400"></span>
            </div>

            <!-- Audit Portal Frame -->
            <div class="w-12 h-14 border border-gold/30 bg-gold/5 flex flex-col items-center justify-center shrink-0 rounded relative shadow-[0_0_10px_rgba(245,183,59,0.08)]">
              <div class="absolute inset-x-0 top-0 h-1 bg-gold"></div>
              <span class="text-[8px] font-black text-gold tracking-widest uppercase scale-[0.8] mb-0.5">AUDIT</span>
              <svg class="w-5 h-5 text-gold" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M9 12l2 2 4-4" />
              </svg>
            </div>

            <!-- Lightning wire -->
            <div class="flex-1 h-[2px] bg-gradient-to-r from-amber-500 to-emerald-500 relative animate-pulse">
              <span class="absolute right-0 top-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-emerald-400"></span>
            </div>

            <!-- Established Pool Coins icon -->
            <div class="flex items-center shrink-0">
              <div class="w-10 h-10 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-400 relative z-10">
                <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div class="w-8 h-8 rounded-full bg-emerald-600/10 border border-emerald-600/20 flex items-center justify-center text-emerald-300 relative z-0 -ml-3">
                <span class="text-[8px] font-black">V3</span>
              </div>
            </div>
          </div>
          
          <div class="text-center text-[10.5px] text-[#999999] leading-relaxed mt-1">
            Audited tokens secure immediate access to DEX's daily fee pool.
          </div>
        </div>
      </div>

      <!-- [CARD 2 IN HTML FLOW] - STREAM 2: LP & MARKETING SPLIT (Desktop: lg:order-4 -> Bottom Right) -->
      <div class="relative border border-white/[0.05] bg-[#0c0c0c]/75 p-6 sm:p-8 rounded-[20px] flex flex-col justify-between shadow-[0_4px_30px_rgba(0,0,0,0.2)] hover:border-gold/30 hover:shadow-[0_0_30px_rgba(245,183,59,0.06)] transition-all duration-300 lg:min-h-[415px] lg:order-4">
        <!-- Badge on Border -->
        <div class="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-gold/10 text-gold text-[9px] font-black uppercase tracking-widest px-4 py-1 border border-gold/30 backdrop-blur-md rounded-md whitespace-nowrap">
          LP & Marketing Split
        </div>

        <div class="mt-2">
          <h3 class="font-display font-black text-white text-[15px] uppercase tracking-wider mb-4">
            2. LP/MARKETING FEE UTILIZATION
          </h3>
          <p class="text-text-muted text-[13px] leading-relaxed mb-4">
            <strong>LP/Marketing Fee split:</strong> $1500 builds token TVL; $3500 adds ETH/USDC V3 LP to compound.
          </p>
        </div>

        <!-- Split calculator diagram matching layout in image 1 -->
        <div class="bg-black/45 border border-white/[0.03] rounded-xl p-4 flex flex-col gap-4">
          <div>
            <div class="flex justify-between items-center mb-1">
              <span class="text-[10px] text-text-dim font-bold uppercase tracking-wider">Fee Calculator:</span>
              <span class="text-xs font-bold text-white">Marketing Setup Fee: <strong class="text-gold font-display font-black">${{ totalFee() | number }}</strong></span>
            </div>
            <input type="range" min="1000" max="10000" step="500" [value]="totalFee()"
              (input)="onFeeSliderChange($event)"
              class="w-full h-1 bg-white/10 rounded-lg appearance-none cursor-pointer accent-gold" />
          </div>

          <div class="flex flex-col gap-2 bg-[#050505]/40 p-2.5 rounded-lg border border-white/[0.03]">
            <!-- Split 1 -->
            <div class="flex items-center justify-between text-[11px]">
              <span class="text-text-muted flex items-center gap-1.5">
                <span class="w-1.5 h-1.5 rounded-full bg-blue-400"></span>
                $1,500 Split TVL Pool
              </span>
              <span class="font-black text-white">${{ tokenTvlSplit() | number }}</span>
            </div>
            
            <!-- Split 2 -->
            <div class="flex items-center justify-between text-[11px]">
              <span class="text-gold flex items-center gap-1.5">
                <span class="w-1.5 h-1.5 rounded-full bg-gold"></span>
                $3,500 Compound ETH/USDC
              </span>
              <span class="font-black text-gold">${{ dexV3Split() | number }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- [CARD 3 IN HTML FLOW] - STREAM 3: USER ATTRACTION (Desktop: lg:order-3 -> Bottom Left) -->
      <div class="relative border border-white/[0.05] bg-[#0c0c0c]/75 p-6 sm:p-8 rounded-[20px] flex flex-col justify-between shadow-[0_4px_30px_rgba(0,0,0,0.2)] hover:border-gold/30 hover:shadow-[0_0_30px_rgba(245,183,59,0.06)] transition-all duration-300 lg:min-h-[415px] lg:order-3">
        <!-- Badge on Border -->
        <div class="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-gold/10 text-gold text-[9px] font-black uppercase tracking-widest px-4 py-1 border border-gold/30 backdrop-blur-md rounded-md whitespace-nowrap">
          User Attraction
        </div>

        <div class="mt-2">
          <h3 class="font-display font-black text-white text-[15px] uppercase tracking-wider mb-4">
            3. ENHANCED USER PARTICIPATION
          </h3>
          <p class="text-text-muted text-[13px] leading-relaxed mb-6">
            <strong>Pair with WETH:</strong> Earning monthly ETH/USDC rewards from DEX's incentive programs directly draws user interest and boosts active pool liquidity.
          </p>
        </div>

        <!-- Visual layout showing users connected to pool -->
        <div class="bg-black/45 border border-white/[0.03] rounded-xl p-4 flex flex-col gap-3">
          <div class="flex justify-between items-center text-[10px] text-text-dim font-bold uppercase tracking-wider px-1">
            <span>User Cluster</span>
            <span>Monthly WETH Incentives</span>
          </div>

          <div class="relative h-[130px] overflow-hidden bg-black/45 border border-white/[0.03] rounded-lg">
            <!-- Inline styles for custom keyframes -->
            <style>
              @keyframes flow-tl {
                0% { transform: translate(0, 0) scale(0.5); opacity: 0; }
                15% { opacity: 1; }
                85% { opacity: 1; }
                100% { transform: translate(-72px, -35px) scale(0.9); opacity: 0; }
              }
              @keyframes flow-tr {
                0% { transform: translate(0, 0) scale(0.5); opacity: 0; }
                15% { opacity: 1; }
                85% { opacity: 1; }
                100% { transform: translate(72px, -35px) scale(0.9); opacity: 0; }
              }
              @keyframes flow-bl {
                0% { transform: translate(0, 0) scale(0.5); opacity: 0; }
                15% { opacity: 1; }
                85% { opacity: 1; }
                100% { transform: translate(-72px, 35px) scale(0.9); opacity: 0; }
              }
              @keyframes flow-br {
                0% { transform: translate(0, 0) scale(0.5); opacity: 0; }
                15% { opacity: 1; }
                85% { opacity: 1; }
                100% { transform: translate(72px, 35px) scale(0.9); opacity: 0; }
              }
              .coin-flow-tl { animation: flow-tl 2.5s linear infinite; }
              .coin-flow-tr { animation: flow-tr 2.8s linear infinite; }
              .coin-flow-bl { animation: flow-bl 2.2s linear infinite; }
              .coin-flow-br { animation: flow-br 2.6s linear infinite; }
            </style>

            <!-- Background lines from center to four corners -->
            <svg class="absolute inset-0 w-full h-full pointer-events-none opacity-20" viewBox="0 0 280 130" preserveAspectRatio="none">
              <line x1="140" y1="65" x2="45" y2="25" stroke="#F5B73B" stroke-width="1.5" stroke-dasharray="3 3" />
              <line x1="140" y1="65" x2="235" y2="25" stroke="#F5B73B" stroke-width="1.5" stroke-dasharray="3 3" />
              <line x1="140" y1="65" x2="45" y2="105" stroke="#F5B73B" stroke-width="1.5" stroke-dasharray="3 3" />
              <line x1="140" y1="65" x2="235" y2="105" stroke="#F5B73B" stroke-width="1.5" stroke-dasharray="3 3" />
            </svg>

            <!-- Four Corner Users -->
            <!-- Top Left -->
            <div class="absolute top-2.5 left-5 flex items-center gap-2">
              <div class="w-8 h-8 rounded-full bg-blue-500/10 border border-blue-500/30 flex items-center justify-center text-blue-400">
                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <span class="text-[9.5px] font-bold text-text-dim uppercase tracking-wider hidden sm:inline">U1</span>
            </div>

            <!-- Top Right -->
            <div class="absolute top-2.5 right-5 flex items-center gap-2">
              <span class="text-[9.5px] font-bold text-text-dim uppercase tracking-wider hidden sm:inline">U2</span>
              <div class="w-8 h-8 rounded-full bg-purple-500/10 border border-purple-500/30 flex items-center justify-center text-purple-400">
                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
            </div>

            <!-- Bottom Left -->
            <div class="absolute bottom-2.5 left-5 flex items-center gap-2">
              <div class="w-8 h-8 rounded-full bg-indigo-500/10 border border-indigo-500/30 flex items-center justify-center text-indigo-400">
                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <span class="text-[9.5px] font-bold text-text-dim uppercase tracking-wider hidden sm:inline">U3</span>
            </div>

            <!-- Bottom Right -->
            <div class="absolute bottom-2.5 right-5 flex items-center gap-2">
              <span class="text-[9.5px] font-bold text-text-dim uppercase tracking-wider hidden sm:inline">U4</span>
              <div class="w-8 h-8 rounded-full bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center text-emerald-400">
                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
            </div>

            <!-- Central Liquidity Pool (New Pair / WETH) -->
            <div class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10 shrink-0">
              <div class="w-16 h-16 rounded-full border-2 border-gold bg-[#0c0c0c] flex flex-col items-center justify-center shadow-[0_0_20px_rgba(245,183,59,0.12)]">
                <span class="text-[7.5px] font-black text-gold uppercase tracking-wider">NEW PAIR</span>
                <span class="text-[9px] font-black text-white mt-0.5">WETH</span>
              </div>
            </div>

            <!-- Animated flowing coin particles -->
            <div class="absolute left-[47%] top-[40%] pointer-events-none coin-flow-tl">
              <div class="w-3.5 h-3.5 rounded-full bg-gold border border-amber-600 flex items-center justify-center text-[7px] text-black font-black">$</div>
            </div>
            <div class="absolute left-[47%] top-[40%] pointer-events-none coin-flow-tr">
              <div class="w-3.5 h-3.5 rounded-full bg-blue-500 border border-blue-600 flex items-center justify-center text-[6.5px] text-white font-black">W</div>
            </div>
            <div class="absolute left-[47%] top-[40%] pointer-events-none coin-flow-bl">
              <div class="w-3.5 h-3.5 rounded-full bg-gold border border-amber-600 flex items-center justify-center text-[7px] text-black font-black">$</div>
            </div>
            <div class="absolute left-[47%] top-[40%] pointer-events-none coin-flow-br">
              <div class="w-3.5 h-3.5 rounded-full bg-blue-500 border border-blue-600 flex items-center justify-center text-[6.5px] text-white font-black">W</div>
            </div>
          </div>
        </div>
      </div>

      <!-- [CARD 4 IN HTML FLOW] - STREAM 4: COMPOUNDING FUND (Desktop: lg:order-1 -> Top Left) -->
      <div class="relative border border-white/[0.05] bg-[#0c0c0c]/75 p-6 sm:p-8 rounded-[20px] flex flex-col justify-between shadow-[0_4px_30px_rgba(0,0,0,0.2)] hover:border-gold/30 hover:shadow-[0_0_30px_rgba(245,183,59,0.06)] transition-all duration-300 lg:min-h-[415px] lg:order-1">
        <!-- Badge on Border -->
        <div class="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-gold/10 text-gold text-[9px] font-black uppercase tracking-widest px-4 py-1 border border-gold/30 backdrop-blur-md rounded-md whitespace-nowrap">
          Compounding Fund
        </div>
        
        <div class="mt-2">
          <h3 class="font-display font-black text-white text-[15px] uppercase tracking-wider mb-4">
            4. MICRO CAP FUND HANDOVER (5 MONTHS)
          </h3>
          <p class="text-text-muted text-[13px] leading-relaxed mb-6">
            <strong>5-Month Growth:</strong> $3,500 in ETH/USDC V3 Compounds to $30,000+. LP position is handed to creator to become a Crypto Micro Cap Fund.
          </p>
        </div>

        <!-- Compounding Visualization diagram -->
        <div class="bg-black/45 border border-white/[0.03] rounded-xl p-4 flex flex-col gap-4">
          <div class="flex items-center justify-between">
            <span class="text-[10px] text-gold font-bold uppercase tracking-wider flex items-center gap-1.5">
              <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
              </svg>
              Creator's Micro Cap Fund
            </span>
            <span class="text-[10px] text-text-dim font-bold">Month 5: $30,000+</span>
          </div>

          <!-- Bar Growth Curve -->
          <div class="flex justify-between items-end h-20 gap-2 px-1 relative">
            <svg class="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 200 80" preserveAspectRatio="none">
              <path d="M 10 70 Q 100 65 190 10" fill="none" stroke="rgba(245, 183, 59, 0.4)" stroke-width="2" />
            </svg>
            
            @for (growth of growthData(); track growth.month) {
            <div (click)="selectMonth(growth.month)" class="flex-1 flex flex-col items-center justify-end h-full group cursor-pointer relative z-10">
              <span class="text-[9px] font-bold text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200 mb-1 absolute -top-4 bg-black border border-gold/30 px-1.5 py-0.5 rounded">
                ${{ growth.value | number }}
              </span>
              <div class="w-full rounded-t transition-all duration-300"
                [class.bg-gold]="selectedMonth() === growth.month"
                [class.bg-white/10]="selectedMonth() !== growth.month"
                [class.group-hover:bg-gold/45]="selectedMonth() !== growth.month"
                [style.height.%]="(growth.value / 30000) * 100"></div>
              <span class="text-[9px] font-bold text-text-dim mt-1.5">M{{ growth.month }}</span>
            </div>
            }
          </div>

          <!-- Stage details card -->
          <div class="p-3 bg-[#050505]/40 border border-white/[0.04] rounded-lg text-[11.5px] leading-relaxed">
            <div class="flex justify-between font-black text-gold mb-1">
              <span>Month {{ selectedMonth() }} - {{ activeGrowthDetail().status }}</span>
              <span>${{ activeGrowthDetail().value | number }}+</span>
            </div>
            <p class="text-[#999999]">{{ activeGrowthDetail().description }}</p>
          </div>
        </div>
      </div>

    </div>

    <!-- Banner footer text exactly as Image 1 -->
    <div class="text-center mt-16 pt-6 border-t border-white/[0.04] text-[11.5px] text-text-dim tracking-[0.15em] uppercase font-black relative z-10">
      DEX - Empowering the next generation of tokens.
    </div>
  </div>

  <!-- ================= SECTION 2: LEARNING & CONCENTRATED LIQUIDITY ================= -->
  <div class="relative">
    
    <!-- Clean Homepage-Style Header Section 2 -->
    <div class="text-center mb-16 flex flex-col items-center gap-4">
      <h2 class="font-display font-black text-2xl sm:text-3xl text-white uppercase tracking-wider">
        DEX Advantage: Learning
      </h2>
      <span class="w-16 h-1 bg-gold rounded-full shadow-[0_0_10px_rgba(245,183,59,0.5)]"></span>
      <p class="text-[11px] sm:text-xs text-text-muted uppercase tracking-[0.2em] font-semibold mt-1">
        Concentrated Liquidity & Academy Strategies
      </p>
    </div>

    <!-- 2 Column Workspace (Learning Hub vs. Liquidity Pools) -->
    <div class="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch mb-8 relative z-10">
      
      <!-- LEFT COLUMN: LEARNING HUB (col-span-5) -->
      <div class="lg:col-span-5 border border-white/[0.05] bg-[#0c0c0c]/75 rounded-[20px] p-6 flex flex-col justify-between shadow-[0_4px_25px_rgba(0,0,0,0.2)] hover:border-gold/15 transition-colors duration-300">
        <div>
          <div class="text-center border-b border-white/[0.05] pb-4 mb-6">
            <h3 class="font-display font-black text-[14px] text-gold uppercase tracking-widest">
              LEARNING HUB
            </h3>
          </div>

          <!-- Vertical Interactive topics list -->
          <div class="flex flex-col gap-3.5 mb-6">
            @for (topic of academyTopics(); track topic.id) {
            <div (click)="setAcademyTopic(topic.id)"
              class="p-4 rounded-xl border transition-all duration-200 cursor-pointer flex items-center justify-between"
              [class.bg-gold/5]="activeAcademyTopic() === topic.id"
              [class.border-gold/30]="activeAcademyTopic() === topic.id"
              [class.bg-[#050505]/40]="activeAcademyTopic() !== topic.id"
              [class.border-white/[0.04]]="activeAcademyTopic() !== topic.id"
              [class.hover:border-gold/20]="activeAcademyTopic() !== topic.id">
              <div>
                <h4 class="font-display font-black text-[12px] sm:text-[13px] uppercase tracking-wider"
                  [class.text-gold]="activeAcademyTopic() === topic.id"
                  [class.text-white]="activeAcademyTopic() !== topic.id">
                  {{ topic.title }}
                </h4>
                <p class="text-[10.5px] text-text-dim mt-0.5 leading-snug">{{ topic.short }}</p>
              </div>

              <svg class="w-4 h-4 transition-transform duration-200" [class.text-gold]="activeAcademyTopic() === topic.id"
                [class.text-text-dim]="activeAcademyTopic() !== topic.id"
                [class.translate-x-1]="activeAcademyTopic() === topic.id" fill="none" viewBox="0 0 24 24"
                stroke="currentColor" stroke-width="2.5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </div>
            }
          </div>
        </div>

        <!-- Content details panel -->
        <div class="p-4.5 bg-black/45 border border-white/[0.04] rounded-xl text-[11.5px] leading-relaxed text-[#999999] flex flex-col gap-3">
          <div>
            <h5 class="text-white font-bold uppercase tracking-wider mb-1.5">{{ currentAcademyContent().title }} Details</h5>
            {{ currentAcademyContent().full }}
          </div>
          <div *ngIf="currentAcademyContent().videoUrl" class="mt-2 pt-2 border-t border-white/5 flex items-center">
            <a [href]="currentAcademyContent().videoUrl" target="_blank"
              class="inline-flex items-center gap-1.5 text-gold hover:text-gold/80 font-bold transition-colors">
              <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                <path stroke-linecap="round" stroke-linejoin="round" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Watch Video Tutorial
            </a>
          </div>
        </div>
      </div>

      <!-- RIGHT COLUMN: LIQUIDITY POOLS (col-span-7) -->
      <div class="lg:col-span-7 border border-white/[0.05] bg-[#0c0c0c]/75 rounded-[20px] p-6 flex flex-col justify-between shadow-[0_4px_25px_rgba(0,0,0,0.2)] hover:border-gold/15 transition-colors duration-300">
        <div>
          <div class="flex justify-between items-center border-b border-white/[0.05] pb-4 mb-6">
            <h3 class="font-display font-black text-[14px] text-gold uppercase tracking-widest">
              LIQUIDITY POOLS
            </h3>
            
            <!-- Range Selection pills -->
            <div class="flex rounded-lg bg-black border border-white/[0.05] p-1 gap-1 shrink-0">
              @for (mode of ['narrow', 'balanced', 'wide']; track mode) {
              <button (click)="setRangeMode(mode)"
                class="px-2.5 py-1 text-[9px] uppercase font-black rounded transition-colors"
                [class.bg-gold]="activeRangeMode() === mode" 
                [class.text-black]="activeRangeMode() === mode"
                [class.text-[#999999]]="activeRangeMode() !== mode">
                {{ mode }}
              </button>
              }
            </div>
          </div>

          <!-- Pool specifications -->
          <div class="grid grid-cols-2 sm:grid-cols-4 gap-3.5 mb-6">
            <div class="bg-black/35 rounded-xl border border-white/[0.03] p-3 text-center">
              <div class="text-[9px] text-text-dim uppercase font-bold tracking-wider">Range Width</div>
              <div class="text-sm font-black text-white mt-1.5">{{ rangeConfig().rangeText }}</div>
            </div>
            <div class="bg-black/35 rounded-xl border border-white/[0.03] p-3 text-center">
              <div class="text-[9px] text-text-dim uppercase font-bold tracking-wider">Efficiency</div>
              <div class="text-sm font-black text-gold mt-1.5">{{ rangeConfig().multiplier }}</div>
            </div>
            <div class="bg-black/35 rounded-xl border border-white/[0.03] p-3 text-center">
              <div class="text-[9px] text-text-dim uppercase font-bold tracking-wider">Est. APY</div>
              <div class="text-sm font-black text-white mt-1.5">{{ rangeConfig().baseApy }}</div>
            </div>
            <div class="bg-black/35 rounded-xl border border-white/[0.03] p-3 text-center flex flex-col justify-center">
              <span class="text-[9px] text-text-dim uppercase font-bold tracking-wider mb-1">Risk</span>
              <span class="text-[8.5px] font-black uppercase tracking-wide border px-1.5 py-0.5 rounded text-center truncate {{ rangeConfig().riskColor }}">
                {{ activeRangeMode() }}
              </span>
            </div>
          </div>

          <p class="text-[12px] text-[#999999] leading-relaxed mb-6 pl-3 border-l-2 border-gold">
            {{ rangeConfig().description }}
          </p>

          <!-- Interactive simulated Dashboard representation -->
          <div class="border border-gold/15 rounded-2xl p-4.5 bg-black/60 shadow-[0_0_25px_rgba(245,183,59,0.04)] relative overflow-hidden">
            <div class="absolute -right-16 -top-16 w-36 h-36 bg-gold/5 rounded-full blur-2xl"></div>

            <div class="flex justify-between items-center border-b border-gold/10 pb-3 mb-4">
              <div class="flex items-center gap-2">
                <span class="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-ping"></span>
                <span class="w-1.5 h-1.5 rounded-full bg-emerald-500 relative"></span>
                <span class="text-[9px] uppercase font-black tracking-widest text-gold">DEX LIVE EARNING DASHBOARD</span>
              </div>
              
              <!-- Compounding switch -->
              <button (click)="toggleReinvest()"
                class="flex items-center gap-1.5 px-3 py-1 rounded bg-black border border-white/[0.05] hover:border-gold/30 transition-all cursor-pointer">
                <div class="w-3 h-3 rounded-full border transition-all duration-200 flex items-center justify-center"
                  [class.bg-emerald-500]="isReinvesting()" 
                  [class.border-emerald-500]="isReinvesting()"
                  [class.border-white/20]="!isReinvesting()">
                  @if (isReinvesting()) {
                  <svg class="w-2 h-2 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="4">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                  }
                </div>
                <span class="text-[9px] text-white uppercase font-bold tracking-wider">Auto-Compound</span>
              </button>
            </div>

            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div>
                <div class="text-[9px] font-bold text-text-dim uppercase tracking-wider">Stream 1: DEX Fees (Daily)</div>
                <div class="text-base font-black text-white mt-0.5 font-display">
                  ${{ simAccumulatedFees() | number:'1.4-4' }}
                </div>
              </div>
              <div>
                <div class="text-[9px] font-bold text-text-dim uppercase tracking-wider">Stream 2: DEX Rewards LP Token</div>
                <div class="text-base font-black text-gold mt-0.5 font-display">
                  {{ simDexRewards() | number:'1.4-4' }} DEX
                </div>
              </div>
            </div>

            <div class="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-4 bg-black/60 border border-white/[0.04] p-3 rounded-xl">
              <div>
                <div class="text-[8.5px] font-bold text-text-dim uppercase tracking-wider">Total Value Compounded</div>
                <div class="text-xs font-bold text-white mt-0.5">
                  ${{ totalSimCompounded() | number:'1.2-2' }} TVL Reinvested
                </div>
              </div>
              <button (click)="claimSimulatedRewards()"
                class="btn-gold px-4 py-2 text-[10.5px] font-extrabold uppercase tracking-widest rounded-lg text-center">
                Claim Accumulated Yield
              </button>
            </div>
          </div>
        </div>
      </div>

    </div>

    <!-- The Double Compound Advantage Callout footer (Clean and premium home-page style panel) -->
    <div class="border-l-4 border-gold bg-[#0c0c0c]/80 p-5 rounded-2xl border border-white/[0.03] shadow-[0_4px_30px_rgba(0,0,0,0.4)] relative z-10">
      <h3 class="font-display font-black text-gold text-[13.5px] sm:text-[15px] uppercase tracking-wider mb-2">
        THE DOUBLE COMPOUND ADVANTAGE:
      </h3>
      <ul class="list-disc list-inside text-[11px] sm:text-[12px] text-white font-bold leading-relaxed flex flex-col gap-1 px-2">
        <li>(1) EARN FEES EVERY DAY FROM PRIMARY POOLS (UNISWAP & PANCAKESWAP).</li>
        <li>(2) EARN THE DEX REWARDS LP TOKEN WHICH <span class="text-gold font-black">*ALSO*</span> COMPOUNDS AND EARNS FEE REVENUES!</li>
      </ul>
    </div>
  </div>

</section>